/* eslint-disable @typescript-eslint/no-explicit-any */
import GoalSkeleton from "@/components/Common/Skeletons/GoalSkeleton";
import { isEnrolledStatus } from "@/types/employeeState";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllBeneficiariesData,
  getEnrolledBeneficiaries,
} from "../../../services/beneficiaries";
import { RootState } from "../../../store";
import { clearSelectedBeneficiary } from "../../../store/beneficiarySlice";
import {
  formatGoalDescription,
  formatMoney,
} from "../../../utils/generalUtilities";

interface GoalsSavingsBeneficiaryData {
  minorAccountID: string;
  firstName: string;
  lastName: string;
  goal: string;
  amountRequired: number;
  timeline: string;
  marketValueAsOfDate: number;
  employeeMarketValue: number;
  employerMarketValue: number;
  totalInvestmentAmount: number;
}

const GoalSettings: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user);
  const [loading, setLoading] = useState<boolean>(true);
  const [beneficiaries, setBeneficiaries] = useState<
    GoalsSavingsBeneficiaryData[]
  >([]);

  const handleFetchGoals = async () => {
    setLoading(true);
    try {
      dispatch(clearSelectedBeneficiary());
      if (user.id) {
        const [responseFromAccSystemAPI, responseFromEmployeeAPI] =
          await Promise.all([
            getEnrolledBeneficiaries(user.employeeID),
            getAllBeneficiariesData(user.employeeID, user.id),
          ]);

        const filteredBeneficiaries = responseFromEmployeeAPI.data.filter(
          (beneficiary: any) =>
            isEnrolledStatus(beneficiary.minorEnrolledStatus),
        );

        const mergedData = filteredBeneficiaries.map((beneficiary: any) => {
          const accountData = responseFromAccSystemAPI.data.find(
            (account: any) =>
              account.minorAccountId === beneficiary.minorAccountID,
          );

          return {
            ...beneficiary,
            marketValueAsOfDate: accountData?.marketValueAsOfDate || 0,
            employeeMarketValue: accountData?.employeeMarketValue || 0,
            employerMarketValue: accountData?.employerMarketValue || 0,
            totalInvestmentAmount: accountData?.totalInvestmentAmount || 0,
          };
        });

        setBeneficiaries(mergedData);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleFetchGoals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const calculateProgressPercentage = (
    currentValue: number | undefined | null,
    targetValue: number | undefined | null,
  ) => {
    const current = currentValue || 0;
    const target = targetValue || 0;
    if (target === 0) return 0;
    const percentage = (current / target) * 100;
    return Math.min(Math.max(0, percentage), 100);
  };

  return (
    <>
      {loading ? (
        <GoalSkeleton />
      ) : (
        <>
          <div>
            {beneficiaries.map((beneficiary: GoalsSavingsBeneficiaryData) => {
              const progressPercentage = calculateProgressPercentage(
                beneficiary.marketValueAsOfDate,
                beneficiary.amountRequired,
              );

              return (
                <div key={beneficiary.minorAccountID}>
                  <div className="p-[32px]">
                    <div className="flex justify-between items-center">
                      <p className="text-lg font-semibold">
                        {formatGoalDescription(beneficiary.goal)}
                      </p>
                    </div>

                    <p className="mt-3 text-[25px] font-semibold text-brand-default">
                      {progressPercentage.toFixed(2)}%
                    </p>

                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-brand-default h-2 rounded-full"
                          style={{ width: `${progressPercentage}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="mt-4 space-y-2 text-black">
                      <div className="flex justify-between">
                        <span>{t("savings_settings.goals.beneficiary")}</span>
                        <span>
                          {beneficiary.firstName} {beneficiary.lastName}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t("savings_settings.goals.deadline")}</span>
                        <span>
                          {beneficiary.timeline.split("T")[0].split("-")[0]}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t("savings_settings.goals.amount")}</span>
                        <span>
                          $ {formatMoney(beneficiary.amountRequired || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t("savings_settings.goals.progress")}</span>
                        <span>
                          ${" "}
                          {formatMoney(
                            Math.trunc(beneficiary.marketValueAsOfDate) || 0,
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                  {beneficiaries.length > 1 && <hr />}
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );
};

export default GoalSettings;
